import type { ICart } from "../types/api/cart";
import type { ICustomerQuery, IOrdersEdgesNode } from "../types/api/customer";
import type { ICaaCustomer, ICaaOrder } from "../types/api/customer-account";
import type { IProductDetailQuery } from "../types/api/product";

export const checkProductLimitReached = ({
  product,
  cart,
  customer,
}: {
  product: IProductDetailQuery["product"];
  cart: ICart["cart"];
  customer: ICustomerQuery["customer"] | null;
}): boolean => {
  // 상품 개수 제한
  const quantityLimitProduct = parseInt(product?.quantityLimitProduct?.value || "-1");
  if (quantityLimitProduct > 0) {
    // 카트에 해당 상품이 담겨있는지 확인
    const cartLineItems = cart.lines.edges.filter(({ node }) => product?.id === node.merchandise.product.id);
    // 카트에 담긴 상품의 수량
    const cartLineItemCount = cartLineItems.reduce((prev, curr) => prev + curr.node.quantity, 0);

    // 구매 내역에 있는지 확인 (패밀리세일 제외, currentQuantity로 취소/환불/부분환불 자동 반영)
    const itemInOrderHistory =
      customer?.orders.edges
        .filter(({ node }: { node: IOrdersEdgesNode }) => !node.isfamilysale)
        .flatMap(({ node }) => node.lineItems.edges)
        .filter(({ node }) => node.variant?.product.id === product?.id) ?? [];

    const totalOrderedProductCount = itemInOrderHistory.reduce((total, { node }) => total + (node.currentQuantity || 0), 0);

    return cartLineItemCount + totalOrderedProductCount >= quantityLimitProduct;
  }

  return false;
};

export const checkVariantLimitReached = ({
  product,
  customer,
  selectedOption,
  cart,
}: {
  product: IProductDetailQuery["product"];
  customer: ICustomerQuery["customer"] | null;
  selectedOption: string;
  cart: ICart["cart"];
}): boolean => {
  // 상품 variant 개수 제한
  const quantityLimitVariant = parseInt(product?.quantityLimit?.value || "-1");
  if (quantityLimitVariant > 0) {
    const cartLineVariantItems = cart.lines.edges.filter(({ node }) => selectedOption === node.merchandise.id);
    const cartLineVariantsItemCount = cartLineVariantItems.reduce((acc, curr) => acc + curr.node.quantity, 0);

    // 구매 내역에 있는지 확인 (패밀리세일 제외, currentQuantity로 취소/환불/부분환불 자동 반영)
    const itemInOrderHistory =
      customer?.orders.edges
        .filter(({ node }: { node: IOrdersEdgesNode }) => !node.isfamilysale)
        .flatMap(({ node }) => node.lineItems.edges)
        .filter(({ node }) => selectedOption === node.variant?.id) ?? [];

    const totalOrderedProductCount = itemInOrderHistory.reduce((total, { node }) => total + (node.currentQuantity || 0), 0);

    return cartLineVariantsItemCount + totalOrderedProductCount >= quantityLimitVariant;
  }

  return false;
};

/**
 * classic checkProductLimitReached의 CAA(Customer Account API) 대응
 * 주문 라인 매칭에 flat `productId`를, 취소/환불 반영에 `refundableQuantity`를 사용
 */
export const checkProductLimitReachedCaa = ({
  product,
  cart,
  customer,
}: {
  product: IProductDetailQuery["product"];
  cart: ICart["cart"];
  customer: ICaaCustomer | null;
}): boolean => {
  const quantityLimitProduct = parseInt(product?.quantityLimitProduct?.value || "-1");
  if (quantityLimitProduct > 0) {
    const cartLineItems = cart.lines.edges.filter(({ node }) => product?.id === node.merchandise.product.id);
    const cartLineItemCount = cartLineItems.reduce((prev, curr) => prev + curr.node.quantity, 0);

    // 구매 내역에 있는지 확인 (패밀리세일 제외, refundableQuantity로 취소/환불/부분환불 반영)
    const itemInOrderHistory =
      customer?.orders?.edges
        .filter(({ node: order }: { node: ICaaOrder }) => order.isfamilysale?.value !== "true")
        .flatMap(({ node: order }) => order.lineItems.edges)
        .filter(({ node: line }) => line.productId === product?.id) ?? [];

    const totalOrderedProductCount = itemInOrderHistory.reduce((total, { node: line }) => total + line.refundableQuantity, 0);

    return cartLineItemCount + totalOrderedProductCount >= quantityLimitProduct;
  }

  return false;
};

/**
 * classic checkVariantLimitReached의 CAA 대응.
 * variant 매칭에 `variant.id` 대신 CAA의 flat `variantId`를 쓴다.
 */
export const checkVariantLimitReachedCaa = ({
  product,
  customer,
  selectedOption,
  cart,
}: {
  product: IProductDetailQuery["product"];
  customer: ICaaCustomer | null;
  selectedOption: string;
  cart: ICart["cart"];
}): boolean => {
  const quantityLimitVariant = parseInt(product?.quantityLimit?.value || "-1");
  if (quantityLimitVariant > 0) {
    const cartLineVariantItems = cart.lines.edges.filter(({ node }) => selectedOption === node.merchandise.id);
    const cartLineVariantsItemCount = cartLineVariantItems.reduce((acc, curr) => acc + curr.node.quantity, 0);

    const itemInOrderHistory =
      customer?.orders?.edges
        .filter(({ node: order }: { node: ICaaOrder }) => order.isfamilysale?.value !== "true")
        .flatMap(({ node: order }) => order.lineItems.edges)
        .filter(({ node: line }) => line.variantId === selectedOption) ?? [];

    const totalOrderedProductCount = itemInOrderHistory.reduce((total, { node: line }) => total + line.refundableQuantity, 0);

    return cartLineVariantsItemCount + totalOrderedProductCount >= quantityLimitVariant;
  }

  return false;
};
